import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { logger } from '@core/utils';
import config from '@core/config';
import { SESSION_EXPIRED_EVENT } from './sessionFetch';
import { getTenantContext, getTenantSignal, TENANT_CHANGED_EVENT } from './tenantContext';

/**
 * Base query with automatic token injection
 */
const rawBaseQuery = fetchBaseQuery({
  baseUrl: config.apiBaseUrl,
  credentials: 'include',
  prepareHeaders: (headers, { getState }) => {
    const csrfToken = getState().auth?.csrfToken;
    const tenant = getTenantContext();
    if (tenant) headers.set('X-Tenant-Context', tenant);
    if (csrfToken) {
      headers.set('X-CSRFToken', csrfToken);
    }

    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }

    return headers;
  },
});

/**
 * Transform error responses to consistent format
 */
function transformError(error) {
  if (error.error) {
    return {
      status: 'FETCH_ERROR',
      error: 'Network error. Please check your connection.',
      originalError: error.error,
    };
  }

  if (error.status) {
    const status = error.status;
    const data = error.data;

    let message = 'An error occurred';

    if (typeof data === 'string') {
      message = data;
    } else if (data?.detail) {
      message = data.detail;
    } else if (data?.message) {
      message = data.message;
    } else if (data?.error) {
      message = data.error;
    } else if (data?.non_field_errors) {
      message = Array.isArray(data.non_field_errors)
        ? data.non_field_errors.join(', ')
        : data.non_field_errors;
    }

    switch (status) {
      case 401:
        message = 'Authentication required. Please log in.';
        break;
      case 403:
        message = 'You do not have permission to perform this action.';
        break;
      case 404:
        message = 'The requested resource was not found.';
        break;
      case 429:
        message = 'Too many requests. Please try again later.';
        break;
      case 500:
      case 502:
      case 503:
      case 504:
        message = 'Server error. Please try again later.';
        break;
    }

    return {
      status,
      error: message,
      data,
      originalError: error,
    };
  }

  return {
    status: 'UNKNOWN_ERROR',
    error: 'An unexpected error occurred',
    originalError: error,
  };
}

/**
 * Enhanced base query with error transformation and FormData support
 */
export const baseQuery = async (args, api, extraOptions) => {
  // For FormData requests, strip the formData flag so fetchBaseQuery
  // receives a normal args object. The browser auto-sets the correct
  // Content-Type with multipart boundary when body is FormData.
  if (typeof args === 'object' && args.formData) {
    args = { ...args };
    delete args.formData;
  }

  const tenant = getTenantContext();
  const tenantSignal = getTenantSignal();
  const result = await rawBaseQuery(args, api, extraOptions);
  if (tenantSignal.aborted || tenant !== getTenantContext()) {
    return { error: { status: 'CUSTOM_ERROR', error: 'Tenant context changed' } };
  }
  if (result.error?.data?.code === 'tenant_context_changed') {
    globalThis.dispatchEvent(new CustomEvent(TENANT_CHANGED_EVENT));
  }

  // A 401 during an established authenticated session means the server-side
  // session is no longer valid. Anonymous auth requests (login, magic link and the
  // initial session probe) must not be mistaken for an expired session.
  if (result.error?.status === 401 && api.getState().auth?.isAuthenticated) {
    globalThis.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
  }

  if (result.error) {
    if (import.meta.env.DEV) {
      logger.error('API Error:', {
        endpoint: typeof args === 'string' ? args : args.url,
        error: result.error,
      });
    }
  }

  if (result.error) {
    result.error = transformError(result.error);
  }

  return result;
};

/**
 * Base query for public endpoints (no authentication)
 */
export const publicBaseQuery = fetchBaseQuery({
  baseUrl: config.apiBaseUrl,
  credentials: 'include',
  prepareHeaders: (headers) => {
    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
    return headers;
  },
});
